import React from 'react';

interface GeometryErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  errorType: 'polygon' | 'trimming' | 'composition' | 'memory' | 'unknown';
  severity: 'recoverable' | 'degraded' | 'critical';
  recoveryAttempts: number;
  wallId?: string;
  operationType?: string;
}

interface GeometryErrorBoundaryProps {
  children: React.ReactNode;
  wallId?: string;
  operationType?: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onRecovery?: (recoveryType: string) => void;
  maxRecoveryAttempts?: number;
  enableDevelopmentMode?: boolean;
  onRecoverPolygon?: (wallId: string) => void;
  onRecoverTrim?: (wallId: string) => void;
  onRecoverComposition?: (wallId: string) => void;
  onRecoverMemory?: () => void;
  onRecoverGeneric?: (wallId: string, operation: string) => void;
}

class GeometryErrorBoundary extends React.Component<GeometryErrorBoundaryProps, GeometryErrorBoundaryState> {
  private errorPatterns = new Map<string, { count: number, lastSeen: number }>();
  private performanceMetrics = {
    errorRecoveryTime: 0,
    successfulRecoveries: 0,
    failedRecoveries: 0,
    memoryUsageAtError: 0
  };

  constructor(props: GeometryErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: 'unknown',
      severity: 'recoverable',
      recoveryAttempts: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<GeometryErrorBoundaryState> {
    const errorType = GeometryErrorBoundary.categorizeError(error);
    const severity = GeometryErrorBoundary.determineSeverity(error, errorType);

    return {
      hasError: true,
      error,
      errorType,
      severity,
      recoveryAttempts: 0
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const startTime = performance.now();

    // Update state with error info
    this.setState({ errorInfo });

    // Track error patterns
    const errorKey = `${error.name}-${error.message.substring(0, 50)}`;
    const existing = this.errorPatterns.get(errorKey) || { count: 0, lastSeen: 0 };
    this.errorPatterns.set(errorKey, {
      count: existing.count + 1,
      lastSeen: Date.now()
    });

    // Log comprehensive error information
    this.logDetailedError(error, errorInfo);

    // Track memory usage at error
    if ((performance as any).memory) {
      this.performanceMetrics.memoryUsageAtError = (performance as any).memory.usedJSHeapSize;
    }

    // Attempt error recovery
    const recoverySuccess = this.attemptErrorRecovery();

    // Track recovery performance
    const recoveryTime = performance.now() - startTime;
    this.performanceMetrics.errorRecoveryTime += recoveryTime;

    if (recoverySuccess) {
      this.performanceMetrics.successfulRecoveries++;
      this.props.onRecovery?.(this.state.errorType);
    } else {
      this.performanceMetrics.failedRecoveries++;
    }

    // Call error callback
    this.props.onError?.(error, errorInfo);
  }

  private static categorizeError(error: Error): GeometryErrorBoundaryState['errorType'] {
    const message = error.message.toLowerCase();
    const stack = error.stack?.toLowerCase() || '';

    if (message.includes('polygons do not intersect') ||
        message.includes('polygon intersection') ||
        message.includes('invalid polygon')) {
      return 'polygon';
    }

    if (message.includes('trim') ||
        stack.includes('trimshapes') ||
        message.includes('clipping')) {
      return 'trimming';
    }

    if (message.includes('compose') ||
        stack.includes('composewallshape') ||
        message.includes('shape composition')) {
      return 'composition';
    }

    if (message.includes('memory') ||
        message.includes('heap') ||
        error.name === 'RangeError') {
      return 'memory';
    }

    return 'unknown';
  }

  private static determineSeverity(error: Error, errorType: GeometryErrorBoundaryState['errorType']): GeometryErrorBoundaryState['severity'] {
    // Critical errors that break core functionality
    if (errorType === 'memory' || error.name === 'RangeError') {
      return 'critical';
    }

    // Polygon and trimming errors usually have fallbacks
    if (errorType === 'polygon' || errorType === 'trimming') {
      return 'recoverable';
    }

    // Composition errors may degrade performance but shouldn't break functionality
    if (errorType === 'composition') {
      return 'degraded';
    }

    return 'degraded';
  }

  private logDetailedError(error: Error, errorInfo: React.ErrorInfo) {
    const context = {
      wallId: this.props.wallId,
      operationType: this.props.operationType,
      errorType: this.state.errorType,
      severity: this.state.severity,
      recoveryAttempts: this.state.recoveryAttempts,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      memoryUsage: (performance as any).memory ? {
        used: (performance as any).memory.usedJSHeapSize,
        total: (performance as any).memory.totalJSHeapSize,
        limit: (performance as any).memory.jsHeapSizeLimit
      } : null,
      errorPatterns: Object.fromEntries(this.errorPatterns),
      performanceMetrics: this.performanceMetrics
    };

    console.error('GeometryErrorBoundary: Caught geometry error', {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack
      },
      errorInfo,
      context
    });

    // In development mode, provide additional debugging information
    if (this.props.enableDevelopmentMode) {
      console.group('🔧 Geometry Error Debug Information');
      console.log('Error Type:', this.state.errorType);
      console.log('Severity:', this.state.severity);
      console.log('Wall ID:', this.props.wallId);
      console.log('Operation:', this.props.operationType);
      console.log('Error Patterns:', this.errorPatterns);
      console.log('Performance Metrics:', this.performanceMetrics);
      console.groupEnd();
    }
  }

  private attemptErrorRecovery(): boolean {
    const maxAttempts = this.props.maxRecoveryAttempts || 3;

    if (this.state.recoveryAttempts >= maxAttempts) {
      console.warn('GeometryErrorBoundary: Maximum recovery attempts reached');
      return false;
    }

    try {
      switch (this.state.errorType) {
        case 'polygon':
          return this.recoverFromPolygonError();
        case 'trimming':
          return this.recoverFromTrimmingError();
        case 'composition':
          return this.recoverFromCompositionError();
        case 'memory':
          return this.recoverFromMemoryError();
        default:
          return this.recoverFromUnknownError();
      }
    } catch (recoveryError) {
      console.error('GeometryErrorBoundary: Recovery attempt failed', recoveryError);
      return false;
    }
  }

  private recoverFromPolygonError(): boolean {
    // For polygon intersection errors, we can fallback to simplified geometry
    console.log('GeometryErrorBoundary: Attempting polygon error recovery with simplified geometry');

    // Call recovery callback if provided
    if (this.props.onRecoverPolygon && this.props.wallId) {
      this.props.onRecoverPolygon(this.props.wallId);
    }

    return true;
  }

  private recoverFromTrimmingError(): boolean {
    // For trimming errors, we can use untrimmed shapes
    console.log('GeometryErrorBoundary: Attempting trimming error recovery with untrimmed shapes');

    // Call recovery callback if provided
    if (this.props.onRecoverTrim && this.props.wallId) {
      this.props.onRecoverTrim(this.props.wallId);
    }

    return true;
  }

  private recoverFromCompositionError(): boolean {
    // For composition errors, use cached shapes or basic rectangles
    console.log('GeometryErrorBoundary: Attempting composition error recovery with cached geometry');

    // Call recovery callback if provided
    if (this.props.onRecoverComposition && this.props.wallId) {
      this.props.onRecoverComposition(this.props.wallId);
    }

    return true;
  }

  private recoverFromMemoryError(): boolean {
    // For memory errors, trigger garbage collection and clear caches
    console.log('GeometryErrorBoundary: Attempting memory error recovery');

    // Call recovery callback if provided
    if (this.props.onRecoverMemory) {
      this.props.onRecoverMemory();
    }

    // Force garbage collection if available
    if (window.gc) {
      window.gc();
    }

    return true;
  }

  private recoverFromUnknownError(): boolean {
    // For unknown errors, try general recovery strategies
    console.log('GeometryErrorBoundary: Attempting general error recovery');

    // Call recovery callback if provided
    if (this.props.onRecoverGeneric && this.props.wallId && this.props.operationType) {
      this.props.onRecoverGeneric(this.props.wallId, this.props.operationType);
    }

    return true;
  }

  private handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      recoveryAttempts: prevState.recoveryAttempts + 1
    }));
  };

  private renderErrorFallback() {
    const { error, errorType, severity, recoveryAttempts } = this.state;
    const { enableDevelopmentMode, maxRecoveryAttempts = 3 } = this.props;

    if (severity === 'critical') {
      return (
        <div style={{
          padding: '10px',
          border: '2px solid #ff4444',
          borderRadius: '4px',
          backgroundColor: '#ffe6e6',
          color: '#cc0000'
        }}>
          <h3>Critical Geometry Error</h3>
          <p>A critical error occurred in the geometry system. Please refresh the page.</p>
          {enableDevelopmentMode && (
            <details style={{ marginTop: '10px' }}>
              <summary>Error Details</summary>
              <pre style={{ fontSize: '12px', overflow: 'auto' }}>
                {error?.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    if (severity === 'degraded') {
      return (
        <div style={{
          padding: '8px',
          border: '1px solid #ff8800',
          borderRadius: '4px',
          backgroundColor: '#fff3e6',
          color: '#cc6600'
        }}>
          <p style={{ margin: 0, fontSize: '14px' }}>
            ⚠️ Geometry simplified due to {errorType} error. Functionality may be limited.
          </p>
          {recoveryAttempts < maxRecoveryAttempts && (
            <button
              onClick={this.handleRetry}
              style={{
                marginTop: '5px',
                padding: '4px 8px',
                background: '#ff8800',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          )}
        </div>
      );
    }

    // For recoverable errors, show minimal UI
    return (
      <div style={{
        padding: '5px',
        border: '1px solid #44aa44',
        borderRadius: '4px',
        backgroundColor: '#e6ffe6',
        color: '#006600'
      }}>
        <p style={{ margin: 0, fontSize: '12px' }}>
          Geometry recovered from {errorType} error
        </p>
      </div>
    );
  }

  getErrorReport() {
    return {
      errorPatterns: Object.fromEntries(this.errorPatterns),
      performanceMetrics: this.performanceMetrics,
      currentError: this.state.hasError ? {
        type: this.state.errorType,
        severity: this.state.severity,
        message: this.state.error?.message,
        recoveryAttempts: this.state.recoveryAttempts
      } : null
    };
  }

  render() {
    if (this.state.hasError) {
      return this.renderErrorFallback();
    }

    return this.props.children;
  }
}

export default GeometryErrorBoundary;